import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, Trash2, Users, FolderOpen, ListPlus, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetAssignments = ({ asset, employees, projects, isAdmin, onRefresh, navigateTo, onClose }) => {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const emptyForm = { assignment_type: 'project', target_id: '', date_from: '', date_to: '', notes: '', group_name: '', responsible_employee_id: '' };
  const [form, setForm] = useState(emptyForm);
  const [batchSelections, setBatchSelections] = useState([]);
  const [batchType, setBatchType] = useState('project');
  const [batchDateFrom, setBatchDateFrom] = useState('');
  const [batchDateTo, setBatchDateTo] = useState('');
  const [batchNotes, setBatchNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAllEmployees, setShowAllEmployees] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.target_id) { toast.error(t('inv.assign.selectTarget')); return; }
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/assignments`, form);
      toast.success(t('inv.assign.added'));
      setAddOpen(false);
      setForm(emptyForm);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (a) => {
    setEditingAssignment(a);
    setForm({ assignment_type: a.assignment_type, target_id: a.target_id, date_from: a.date_from || '', date_to: a.date_to || '', notes: a.notes || '', group_name: a.group_name || '', responsible_employee_id: a.responsible_employee_id || '' });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/inventory/assets/${asset.id}/assignments/${editingAssignment.id}`, form);
      toast.success(t('inv.assign.updated'));
      setEditOpen(false);
      setEditingAssignment(null);
      setForm(emptyForm);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemove = async (assignmentId) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/assignments/${assignmentId}`);
      toast.success(t('inv.assign.removed'));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleBatchSelection = (id) => {
    setBatchSelections(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBatchAssign = async () => {
    if (batchSelections.length === 0) { toast.error(t('inv.assign.selectAtLeastOne')); return; }
    setSaving(true);
    try {
      const assignments = batchSelections.map(targetId => ({
        assignment_type: batchType,
        target_id: targetId,
        date_from: batchDateFrom || null,
        date_to: batchDateTo || null,
        notes: batchNotes || null,
      }));
      const res = await axios.post(`${API}/inventory/assets/${asset.id}/assignments/batch`, assignments);
      toast.success(t('inv.assign.batchAdded', { count: res.data.added }));
      setBatchOpen(false);
      setBatchSelections([]);
      setBatchDateFrom('');
      setBatchDateTo('');
      setBatchNotes('');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const assignments = asset.assignments || [];
  // A tool that came in for a project belongs to that project's crew.
  // Offering all 200 employees invites the wrong name on the receipt.
  const scopedProjectId = asset.provided_for_project_id
    || assignments.find((a) => a.assignment_type === 'project')?.target_id
    || null;
  const scopedProject = (projects || []).find((p) => p.id === scopedProjectId);
  const teamIds = new Set((scopedProject?.team_members || []).map((m) => m.employee_id));
  const teamEmployees = (employees || []).filter((e) => teamIds.has(e.id));
  // An empty team is not a reason to block the hand-over — fall back to
  // everyone and say so.
  const restricted = !!scopedProject && teamEmployees.length > 0 && !showAllEmployees;
  const employeeOptions = restricted ? teamEmployees : (employees || []);
  const targets = form.assignment_type === 'project' ? (projects || []) : employeeOptions;
  const batchTargets = batchType === 'project' ? (projects || []) : employeeOptions;
  const existingTargetIds = new Set(assignments.filter(a => a.assignment_type === batchType).map(a => a.target_id));

  const TeamScopeNote = () => {
    if (!scopedProject) return null;
    return (
      <p className="mt-1 text-xs text-muted-foreground" data-testid="team-scope-note">
        {teamEmployees.length === 0
          ? t('inv.assign.teamEmpty', { project: scopedProject.name })
          : restricted
            ? t('inv.assign.teamOnly', { project: scopedProject.name, count: teamEmployees.length })
            : t('inv.assign.teamAll', { project: scopedProject.name })}
        {teamEmployees.length > 0 && (
          <button type="button" className="ml-1 underline hover:no-underline"
            onClick={() => setShowAllEmployees((v) => !v)} data-testid="toggle-all-employees">
            {t(showAllEmployees ? 'inv.assign.showTeamOnly' : 'inv.assign.showAllEmployees')}
          </button>
        )}
      </p>
    );
  };

  return (
    <div className="space-y-3" data-testid="asset-assignments">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h4 className="text-sm font-semibold">{t('inv.assign.title', { count: assignments.length })}</h4>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setBatchOpen(true)} data-testid="batch-assign-btn">
              <ListPlus className="w-4 h-4 mr-1" /> {t('inv.assign.batch')}
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-assignment-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('inv.assets.assign')}
            </Button>
          </div>
        )}
      </div>
      {assignments.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.assign.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('inv.common.type')}</TableHead>
              <TableHead>{t('inv.assign.assignedTo')}</TableHead>
              <TableHead className="hidden sm:table-cell">{t('inv.assign.crewResponsible')}</TableHead>
              <TableHead>{t('inv.common.from')}</TableHead>
              <TableHead>{t('inv.common.to')}</TableHead>
              <TableHead>{t('common.notes')}</TableHead>
              {isAdmin && <TableHead className="w-[50px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {assignments.map((a) => (
              <TableRow key={a.id} data-testid={`assignment-row-${a.id}`}>
                <TableCell>
                  <Badge variant="outline">
                    {a.assignment_type === 'project' ? <FolderOpen className="w-3 h-3 mr-1" /> : <Users className="w-3 h-3 mr-1" />}
                    {t(`inv.assign.kind.${a.assignment_type}`, { defaultValue: a.assignment_type })}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium">
                  {navigateTo ? (
                    <button className="text-blue-600 hover:underline" onClick={() => {
                      onClose?.();
                      if (a.assignment_type === 'project') {
                        navigateTo('projects', { searchProject: a.target_name || a.target_id });
                      } else {
                        navigateTo('employees', { searchEmployee: a.target_name || a.target_id });
                      }
                    }} data-testid={`nav-assignment-${a.id}`}>{a.target_name || a.target_id}</button>
                  ) : (a.target_name || a.target_id)}
                  <span className="sm:hidden block text-xs text-muted-foreground">
                    {[a.group_name, a.responsible_employee_name].filter(Boolean).join(' · ')}
                  </span>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-sm">
                  {a.group_name ? <span className="block">{a.group_name}</span> : null}
                  {a.responsible_employee_name
                    ? <span className="block text-xs text-muted-foreground">{t('inv.assign.responsibleLabel')}: {a.responsible_employee_name}</span>
                    : (!a.group_name ? <span className="text-muted-foreground">-</span> : null)}
                </TableCell>
                <TableCell className="text-sm">{a.date_from || '-'}</TableCell>
                <TableCell className="text-sm">{a.ongoing ? <Badge variant="secondary">{t('inv.common.ongoing')}</Badge> : a.date_to || '-'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{a.notes || '-'}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`edit-assignment-${a.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleRemove(a.id)} data-testid={`remove-assignment-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Single Assignment Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-assignment-dialog">
          <DialogHeader><DialogTitle>{t('inv.assign.dialogTitle')}</DialogTitle><DialogDescription className="sr-only">{t('inv.assign.dialogTitle')}</DialogDescription></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <div>
              <Label className="text-xs">{t('inv.common.type')}</Label>
              <Select value={form.assignment_type} onValueChange={(v) => setForm(prev => ({ ...prev, assignment_type: v, target_id: ''  }))}>
                <SelectTrigger data-testid="assignment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">{t('inv.assign.kind.project')}</SelectItem>
                  <SelectItem value="employee">{t('inv.assign.kind.employee')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t(form.assignment_type === 'project' ? 'inv.assign.kind.project' : 'inv.assign.kind.employee')} *</Label>
              <Select value={form.target_id} onValueChange={(v) => setForm(prev => ({ ...prev, target_id: v  }))}>
                <SelectTrigger data-testid="assignment-target-select"><SelectValue placeholder={t('inv.assign.select')} /></SelectTrigger>
                <SelectContent>
                  {targets.map((opt) => (<SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {form.assignment_type === 'employee' && <TeamScopeNote />}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t('inv.common.from')}</Label><Input type="date" value={form.date_from} onChange={(e) => setForm(prev => ({ ...prev, date_from: e.target.value  }))} /></div>
              <div><Label className="text-xs">{t('auto.inv.to')}</Label><Input type="date" value={form.date_to} onChange={(e) => setForm(prev => ({ ...prev, date_to: e.target.value  }))} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('inv.matrix.crewName')}</Label>
                <Input value={form.group_name} onChange={(e) => setForm(prev => ({ ...prev, group_name: e.target.value }))}
                  placeholder={t('inv.assign.nightShiftTeamB')} data-testid="assignment-group-name" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.matrix.responsible')}</Label>
                <Select value={form.responsible_employee_id}
                  onValueChange={(v) => setForm(prev => ({ ...prev, responsible_employee_id: v }))}>
                  <SelectTrigger data-testid="assignment-responsible-select"><SelectValue placeholder={t('inv.assign.nobodyNamedYet')} /></SelectTrigger>
                  <SelectContent>
                    {employeeOptions.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">{t('common.notes')}</Label><Input value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value  }))} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button type="submit" data-testid="save-assignment-btn">{t('inv.assets.assign')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Batch Assignment Dialog */}
      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-lg" data-testid="batch-assignment-dialog">
          <DialogHeader><DialogTitle><ListPlus className="w-4 h-4 inline mr-1" />{t('inv.assign.batchTitle')}</DialogTitle><DialogDescription className="sr-only">{t('inv.assign.batchTitle')}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('inv.assign.assignmentType')}</Label>
              <Select value={batchType} onValueChange={(v) => { setBatchType(v); setBatchSelections([]); }}>
                <SelectTrigger data-testid="batch-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">{t('inv.matrix.projects')}</SelectItem>
                  <SelectItem value="employee">{t('inv.matrix.employees')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t('inv.assign.selectTargets', { kind: t(batchType === 'project' ? 'inv.matrix.projects' : 'inv.matrix.employees'), count: batchSelections.length })}</Label>
              {batchType === 'employee' && <TeamScopeNote />}
              <ScrollArea className="h-[200px] border rounded-md p-2">
                {batchTargets.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No {batchType === 'project' ? 'projects' : 'employees'} available.</p>
                ) : (
                  batchTargets.map((opt) => {
                    const alreadyAssigned = existingTargetIds.has(opt.id);
                    return (
                      <div key={opt.id} className={`flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 ${alreadyAssigned ? 'opacity-50' : ''}`} data-testid={`batch-target-${opt.id}`}>
                        <Checkbox
                          checked={batchSelections.includes(opt.id)}
                          onCheckedChange={() => toggleBatchSelection(opt.id)}
                          disabled={alreadyAssigned}
                          data-testid={`batch-check-${opt.id}`}
                        />
                        <span className="text-sm flex-1">{opt.name}</span>
                        {alreadyAssigned && <Badge variant="secondary" className="text-[10px]">{t('inv.assign.alreadyAssigned')}</Badge>}
                      </div>
                    );
                  })
                )}
              </ScrollArea>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t('inv.common.from')}</Label><Input type="date" value={batchDateFrom} onChange={(e) => setBatchDateFrom(e.target.value)} /></div>
              <div><Label className="text-xs">{t('auto.inv.to')}</Label><Input type="date" value={batchDateTo} onChange={(e) => setBatchDateTo(e.target.value)} /></div>
            </div>
            <div><Label className="text-xs">{t('common.notes')}</Label><Input value={batchNotes} onChange={(e) => setBatchNotes(e.target.value)} placeholder={t('inv.assign.sharedNotesForAllAssignments')} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setBatchOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button onClick={handleBatchAssign} disabled={saving || batchSelections.length === 0} data-testid="save-batch-btn">
                {saving ? t('inv.matrix.assigning') : t('inv.assign.assignToCount', { count: batchSelections.length, kind: t(batchType === 'project' ? 'inv.matrix.projects' : 'inv.matrix.employees') })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Edit Assignment Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-assignment-dialog">
          <DialogHeader><DialogTitle><Pencil className="w-4 h-4 inline mr-1" />{t('inv.assign.editTitle')}</DialogTitle><DialogDescription className="sr-only">{t('inv.assign.editTitle')}</DialogDescription></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            <div>
              <Label className="text-xs">{t('inv.assign.assignedTo')}</Label>
              <p className="text-sm font-medium">{editingAssignment?.target_name || editingAssignment?.target_id}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t('inv.common.from')}</Label><Input type="date" value={form.date_from} onChange={(e) => setForm(prev => ({ ...prev, date_from: e.target.value  }))} /></div>
              <div><Label className="text-xs">{t('auto.inv.to')}</Label><Input type="date" value={form.date_to} onChange={(e) => setForm(prev => ({ ...prev, date_to: e.target.value  }))} /></div>
            </div>
            <div><Label className="text-xs">{t('common.notes')}</Label><Input value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value  }))} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button type="submit" data-testid="update-assignment-btn">{t('inv.common.update')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetAssignments;
